import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useContext
} from 'react';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import {
  createAPI, updateAPI, getListAllAPI,
} from '../../../services';
import {
  SECONDARY_COLOR,
  ATALHO_VOLTAR,
  ATALHO_AVANCAR,
  SECONDARY_COLOR_OFFLINE,
} from '../../../utils';
import { validarCPF, validarCNPJ } from '../../../utils/validadores'

import {
  AutoCompleteLocal,
  AutoCompleteRemoto,
  CepInput,
  CnpjInput,
  CpfInput,
  DateInput,
  IEInput,
  TelefoneInput,
  TextInput,
} from '../../Inputs';
import DialogoAutorizacaoQrcode2 from "../../../components/Dialogos/DialogoAutorizacao2";

import { AppContext } from '../../../app/context';

const FECHAR_VENDA_ACTION = ATALHO_AVANCAR;
const CANCELAR_VENDA_ACTION = ATALHO_VOLTAR;
const ESC_ACTION = 'Escape';

const IND_IE_DEST_CONTRIBUINTE = '1';
const IND_IE_DEST_ISENTO = '2';
const IND_IE_DEST_NAO_CONTRIBUINTE = '9';

const optionsIndIeDest = [
  { value: IND_IE_DEST_CONTRIBUINTE, label: 'Contribuinte ICMS' },
  { value: IND_IE_DEST_ISENTO, label: 'Contribuinte isento de Inscrição' },
  { value: IND_IE_DEST_NAO_CONTRIBUINTE, label: 'Não Contribuinte' },
];

const COMPRADOR_CPF = 'cpf';
const COMPRADOR_CNPJ = 'cnpj';

const GrupoCardEscolha = forwardRef(
  ({ opcoes, valorSelecionado, onChange, handleEnter, handleKey, autoFocus = false }, ref) => {
    const containerRef = useRef(null);
    const [focado, setFocado] = useState(false);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (containerRef.current) containerRef.current.focus();
      },
      select: () => {},
    }));

    useEffect(() => {
      if (autoFocus && containerRef.current) containerRef.current.focus();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function alternar(direcao) {
      const total = opcoes.length;
      const indiceAtual = opcoes.findIndex((opcao) => opcao.valor === valorSelecionado);
      let proximo = indiceAtual === -1 ? -direcao : indiceAtual;
      for (let tentativas = 0; tentativas < total; tentativas += 1) {
        proximo = (proximo + direcao + total) % total;
        if (!opcoes[proximo].disabled) break;
      }
      onChange(opcoes[proximo].valor);
    }

    return (
      <Box
        ref={containerRef}
        tabIndex={0}
        onFocus={() => setFocado(true)}
        onBlur={() => setFocado(false)}
        onKeyDown={(e) => {
          if (e.keyCode === 13) {
            handleEnter();
            return;
          }
          switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
              alternar(-1);
              break;
            case 'ArrowRight':
            case 'ArrowDown':
              alternar(1);
              break;
            default:
              if (handleKey) handleKey(e.keyCode, e.key);
              break;
          }
        }}
        style={{
          display: 'flex',
          gap: '12px',
          padding: '4px',
          borderRadius: '10px',
          boxShadow: focado ? '0 0 0 3px rgba(21,101,192,0.5)' : 'none',
          transition: 'box-shadow 0.15s ease-in-out',
          outline: 'none',
        }}
      >
        {opcoes.map((opcao) => {
          const selecionado = opcao.valor === valorSelecionado;
          return (
            <Box
              key={opcao.valor}
              onClick={() => {
                if (opcao.disabled) return;
                if (containerRef.current) containerRef.current.focus();
                onChange(opcao.valor);
              }}
              style={{
                flex: 1,
                cursor: opcao.disabled ? 'not-allowed' : 'pointer',
                textAlign: 'center',
                padding: '12px 14px',
                borderRadius: '8px',
                border: selecionado ? '2px solid #2e7d32' : '2px solid transparent',
                backgroundColor: opcao.disabled ? '#f5f5f5' : selecionado ? '#43a047' : '#e0e0e0',
                color: opcao.disabled ? '#bdbdbd' : selecionado ? '#fff' : '#757575',
                fontWeight: selecionado ? 'bold' : 'normal',
                boxSizing: 'border-box',
                boxShadow: selecionado ? '0 2px 8px rgba(67,160,71,0.4)' : 'none',
                transition: 'all 0.15s ease-in-out',
              }}
            >
              {opcao.label}
              {selecionado ? ' ✓' : ''}
            </Box>
          );
        })}
      </Box>
    );
  },
);

function validateEmail2(email) {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
}

const useStyles = makeStyles((theme) => ({
  rootDialogo: {
    borderRadius: '25px',
  },
  btnAtions: {
    borderRadius: '25px',
    width: '150px',
  },
  btnAtionsDisabled: {
    borderRadius: '25px',
    width: '150px',
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const NOME_POSITION = 0;
const CPF_POSITION = 1;
const TELEFONE_POSITION = 2;
const EMAIL_POSITION = 3;
const ANIVERSARIO_POSITION = 4;
const RG_POSITION = 5;
const CATEGORIA_POSITION = 6;
const RAZAO_SOCIAL_POSITION = 7;
const NOME_FANTASIA_POSITION = 8;
const CNPJ_POSITION = 9;
const IE_POSITION = 10;
const CIDADE_POSITION = 11;
const BAIRRO_POSITION = 12;
const LOGRADOURO_POSITION = 13;
const NUMERO_POSITION = 14;
const CEP_POSITION = 15;
const IND_IE_DEST_POSITION = 16;
const PRECISA_NFE_POSITION = 17;
const COMPRADOR_POSITION = 18;

const DEFAULT_ID = -1;

const CONSULTOR_PRECOS_ACTION = 'F3';
const FIDELIDADE_ACTION = 'F10';
const BUSCAR_SEFAZ_ACTION = 'F6';

const SidebarInputs = forwardRef(({
  handleClose,
  user_id,
  openConsultorPrecos= () => {},
  usar_biometria = false
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [id, setId] = useState(DEFAULT_ID);
  const [precisaNfe, setPrecisaNfe] = useState(null);
  const [comprador, setComprador] = useState(null);
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [bairro, setBairro] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [rg, setRg] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [ie, setIe] = useState('');
  const [indIeDest, setIndIeDest] = useState(IND_IE_DEST_NAO_CONTRIBUINTE);
  const [email, setEmail] = useState('');
  const [cep, setCep] = useState('');
  const [aniversario, setAniversario] = useState('');
  const [cidade, setCidade] = useState(null);
  const [categoria, setCategoria] = useState(null);
  const [categoriaDefault, setCategoriaDefault] = useState(null);
  const [isFidelidade, setIsFidelidade] = useState(false);

  const refs = useRef([]);
  const [carregando, setCarregando] = useState(false);
  const [consultandoSefaz, setConsultandoSefaz] = useState(false);
  const [dialogoBuscaSefazAberto, setDialogoBuscaSefazAberto] = useState(false);
  const [indiceOpcaoBuscaSefaz, setIndiceOpcaoBuscaSefaz] = useState(0);
  const refsOpcaoBuscaSefaz = useRef([]);
   const refDialogoAutorizacao = useRef(null);

  const { app: { modoOffline } } = useContext(AppContext);

  useEffect(() => {
    if (dialogoBuscaSefazAberto && refsOpcaoBuscaSefaz.current[indiceOpcaoBuscaSefaz]) {
      refsOpcaoBuscaSefaz.current[indiceOpcaoBuscaSefaz].focus();
    }
  }, [dialogoBuscaSefazAberto, indiceOpcaoBuscaSefaz]);

  // Ouve o teclado direto no window (mesmo padrão do TelaCartaoEscolha em
  // DialogoInicioVenda) em vez de depender de qual elemento está com o
  // foco — esse Dialog fica por cima de outro Dialog já aberto, e o foco
  // imperativo num Box específico é disputado pelo TrapFocus do MUI, então
  // às vezes o foco real nem chega a entrar no popup. Todas as teclas são
  // tratadas aqui dentro (inclusive F8/F4) e NADA é repassado pro
  // handleKey/handleActions do diálogo Cliente por trás — senão F4 fecha
  // o popup E cai em cascata fechando o cadastro inteiro também.
  useEffect(() => {
    if (!dialogoBuscaSefazAberto) return undefined;

    function handleKeyDownSefaz(e) {
      const opcoes = getOpcoesBuscaSefaz();
      if (e.keyCode === 13 || e.key === ' ' || e.key === FECHAR_VENDA_ACTION) {
        e.preventDefault();
        if (opcoes[indiceOpcaoBuscaSefaz]) {
          buscarDadosSefaz(opcoes[indiceOpcaoBuscaSefaz].tipo);
        }
        return;
      }
      if (e.key === 'Escape' || e.key === CANCELAR_VENDA_ACTION) {
        setDialogoBuscaSefazAberto(false);
        return;
      }
      const total = opcoes.length;
      if (['ArrowUp', 'ArrowLeft'].includes(e.key)) {
        e.preventDefault();
        setIndiceOpcaoBuscaSefaz((atual) => (atual - 1 + total) % total);
      } else if (['ArrowDown', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        setIndiceOpcaoBuscaSefaz((atual) => (atual + 1) % total);
      }
    }

    window.addEventListener('keydown', handleKeyDownSefaz);
    return () => window.removeEventListener('keydown', handleKeyDownSefaz);
  }, [dialogoBuscaSefazAberto, indiceOpcaoBuscaSefaz]);

  useEffect(() => {
    async function getCategoriaDefault() {
      try {
        const data = await getListAllAPI(
          'categorias_cliente',
          ['id', 'asc'],
          { id: [1] },
          [],
        );
        if (data.data.length > 0) {
          setCategoriaDefault(data.data[0]);
        } else {
          throw 'Erro ao obter a categoria de cliente padrão!';
        }
      } catch (erros) {
        enqueueSnackbar(`${erros}`, {
          variant: 'error',
        });
        handleCloseDialog(-1);
      }
    }
    getCategoriaDefault();
  }, []);

  const inputs = [
    {
      nome: 'nome222',
      defaultValue: '',
      label: 'Nome',
    },
    {
      nome: 'cpf222',
      defaultValue: '',
      label: 'CPF',
    },
    {
      nome: 'telefone222',
      defaultValue: '',
      label: 'Telefone',
    },
    {
      nome: 'bairro222',
      defaultValue: '',
      label: 'Bairro',
    },
    {
      nome: 'logradouro222',
      defaultValue: '',
      label: 'Logradouro',
    },
    {
      nome: 'numero222',
      defaultValue: '',
      label: 'Número',
    },
    {
      nome: 'rg222',
      defaultValue: '',
      label: 'RG',
    },
    {
      nome: 'razao_social222',
      defaultValue: '',
      label: 'Razão Social',
    },
    {
      nome: 'nome_fantasia222',
      defaultValue: '',
      label: 'Nome Fantasia',
    },
    {
      nome: 'cnpj222',
      defaultValue: '',
      label: 'CNPJ',
    },
    {
      nome: 'email222',
      defaultValue: '',
      label: 'Email',
    },
    {
      nome: 'cep222',
      defaultValue: '',
      label: 'CEP',
    },
    {
      nome: 'aniversario222',
      defaultValue: '',
      label: 'Aniversário',
    },
    {
      nome: 'cidade222',
      defaultValue: null,
      label: 'Cidade',
      resource: 'cidades',
      nested: ['estado'],
      filters: { ativo: true },
    },
    {
      nome: 'ie222',
      defaultValue: '',
      label: 'Inscrição Estadual',
    },
    {
      nome: 'Categoria222',
      defaultValue: null,
      label: 'Categoria',
      resource: 'categorias_cliente',
      nested: [],
      filters: { ativo: true },
    },
    {
      nome: 'ind_ie_dest222',
      defaultValue: IND_IE_DEST_NAO_CONTRIBUINTE,
      label: 'Indicador da IE do Destinatário',
    },
  ];

  refs.current = inputs.map(
    (ref, index) => refs.current[index] = React.createRef(),
  );
  refs.current[PRECISA_NFE_POSITION] = refs.current[PRECISA_NFE_POSITION] || React.createRef();
  refs.current[COMPRADOR_POSITION] = refs.current[COMPRADOR_POSITION] || React.createRef();

  function validateNome() {
    let error = '';
    if (!nome) error = 'Este campo é obrigatório';
    else if (nome.length > 80) { error = 'Número de caracteres maior que o permitido (máximo 80)'; }
    return error;
  }

  function validateCpf() {
    let error = '';
    if (!cpf && !cnpj) error = 'Preencha este campo ou o cnpj!';
    else if (cpf) {
      if (cpf.length < 11) error = 'O CPF deve ter 11 dígitos';
      else if (!validarCPF(cpf)) error = 'CPF Inválido';
    }
    return error;
  }

  function validateTelefone() {
    let error = '';
    if (telefone) {
      if (telefone.length < 10) { error = 'O Telefone deve ter 10 ou 11 dígitos'; }
    } else {
      if (isFidelidade) {
        error = 'Este campo é obrigatório';
      }
    }

    return error;
  }

  function validateBairro() {
    let error = '';
    // obrigatório só quando o cadastro precisa suportar NFe (ver montarClienteNFe no worker)
    if (precisaNfe && !bairro) error = 'Este campo é obrigatório';
    else if (bairro) {
      if (bairro.length > 80) { error = 'Número de caracteres maior que o permitido (máximo 80)'; }
    }
    return error;
  }

  function validateLogradouro() {
    let error = '';
    if (precisaNfe && !logradouro) error = 'Este campo é obrigatório';
    else if (logradouro) {
      if (logradouro.length > 80) { error = 'Número de caracteres maior que o permitido (máximo 80)'; }
    }
    return error;
  }

  function validateNumero() {
    let error = '';
    if (numero) {
      if (numero.length > 6) { error = 'Máximo de 6 letras'; }
    }
    return error;
  }

  function validateRg() {
    let error = '';
    if (rg) {
      if (rg.length > 20) { error = 'Número de caracteres maior que o permitido (máximo 20)'; }
    }
    return error;
  }

  function validateRazaoSocial() {
    let error = '';
    // pra NFe, PJ precisa de razão social ou nome fantasia (xNome do destinatário)
    if (precisaNfe && comprador === COMPRADOR_CNPJ && !razaoSocial && !nomeFantasia) {
      error = 'Preencha a Razão Social ou o Nome Fantasia';
    } else if (razaoSocial) {
      if (razaoSocial.length > 80) { error = 'Número de caracteres maior que o permitido (máximo 80)'; }
    }
    return error;
  }

  function validateNomeFantasia() {
    let error = '';
    if (nomeFantasia) {
      if (nomeFantasia.length > 80) { error = 'Número de caracteres maior que o permitido (máximo 80)'; }
    }
    return error;
  }

  function validateCnpj() {
    let error = '';
    if (!cpf && !cnpj) error = 'Preencha este campo ou o cpf!';
    else if (cnpj) {
      if (cnpj.length < 14) error = 'O CNPJ deve ter 14 dígitos';
      else if (!validarCNPJ(cnpj)) error = 'CNPJ Inválido';
    }
    return error;
  }

  function validateComprador() {
    let error = '';
    if (precisaNfe && !comprador) error = 'Selecione se o documento é CPF ou CNPJ';
    return error;
  }

  function validatePrecisaNfe() {
    let error = '';
    if (precisaNfe === null) error = 'Este campo é obrigatório';
    return error;
  }

  function validateEmail() {
    let error = '';
    if (email) {
      if (!validateEmail2(email)) error = 'Email Inválido';
      if (email.length > 80) { error = 'Número de caracteres maior que o permitido (máximo 80)'; }
    }
    return error;
  }

  function validateCep() {
    let error = '';
    if (precisaNfe && !cep) error = 'Este campo é obrigatório';
    else if (cep) {
      if (cep.length < 8) error = 'O CEP deve ter 8 dígitos';
    }
    return error;
  }

  function validateAniversario() {
    let error = '';
    if (isFidelidade) {
      if (!aniversario) error = 'Este campo é obrigatório';
    }
    return error;
  }

  function validateCidade() {
    let error = '';
    if (!cidade) error = 'Este campo é obrigatório';
    return error;
  }

  function validateIE() {
    let error = '';
    if (ie) {
      if (ie.length >= 20) error = 'A Inscrição Estadual deve ser menor que 20 dígitos';
      // isento de IE não pode ter IE preenchida na NFe (rejeição 791)
      else if (indIeDest === IND_IE_DEST_ISENTO) {
        error = 'Cliente isento de IE não pode ter a Inscrição Estadual preenchida';
      }
    } else if (indIeDest === IND_IE_DEST_CONTRIBUINTE) {
      // contribuinte de ICMS precisa ter IE na NFe (rejeição 728)
      error = 'IE é obrigatória para clientes Contribuintes de ICMS';
    }
    return error;
  }

  function validateCategoria() {
    let error = '';
    if (!categoria) error = 'Este campo é obrigatório';
    return error;
  }

  function getErros() {
    const errosOld = [
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ];
    errosOld[0] = validateNome();
    errosOld[1] = validateCpf();
    errosOld[2] = validateTelefone();
    errosOld[3] = validateBairro();
    errosOld[4] = validateLogradouro();
    errosOld[5] = validateNumero();
    errosOld[6] = validateRg();
    errosOld[7] = validateRazaoSocial();
    errosOld[8] = validateNomeFantasia();
    errosOld[9] = validateCnpj();
    errosOld[10] = validateEmail();
    errosOld[11] = validateCep();
    errosOld[12] = validateAniversario();
    errosOld[13] = validateCidade();
    errosOld[14] = validateIE();
    errosOld[15] = validateCategoria();

    return errosOld;
  }

  const erros = getErros();
  const erroComprador = validateComprador();
  const erroPrecisaNfe = validatePrecisaNfe();

  function hasError() {
    for (let i = 0; i < erros.length; i += 1) {
      if (erros[i] !== '') return true;
    }
    return erroComprador !== '' || erroPrecisaNfe !== '';
  }

  const erroExistente = hasError();

  function decompoeData(data) {
    if (data === null || data === '') {
      return {
        dia_aniversario: null,
        mes_aniversario: null,
        ano_aniversario: null,
      };
    }
    const dataAniversarioObj = new Date(moment(new Date(data)).add(1, 'days').format());
    const dia_aniversario = dataAniversarioObj.getDate();
    const mes_aniversario = dataAniversarioObj.getMonth();
    const ano_aniversario = dataAniversarioObj.getFullYear();
    return {
      dia_aniversario,
      mes_aniversario,
      ano_aniversario,
    };
  }

  const handleCloseDialog = (idUpdate) => {
    setOpen(false);
    handleClose(idUpdate);
  };

  async function reiniciar() {
    setId(DEFAULT_ID);
    setPrecisaNfe(null);
    setComprador(null);
    setNome(inputs[0].defaultValue);
    setCpf(inputs[1].defaultValue);
    setTelefone(inputs[2].defaultValue);
    setBairro(inputs[3].defaultValue);
    setLogradouro(inputs[4].defaultValue);
    setNumero(inputs[5].defaultValue);
    setRg(inputs[6].defaultValue);
    setRazaoSocial(inputs[7].defaultValue);
    setNomeFantasia(inputs[8].defaultValue);
    setCnpj(inputs[9].defaultValue);
    setEmail(inputs[10].defaultValue);
    setCep(inputs[11].defaultValue);
    setAniversario(inputs[12].defaultValue);
    setCidade(inputs[13].defaultValue);
    setIe(inputs[14].defaultValue);
    setIndIeDest(inputs[16].defaultValue);
    setCategoria(categoriaDefault);
    setIsFidelidade(false);
    setCarregando(false);
  }

  async function getDataResource(id) {
    try {
      const data = await getListAllAPI(
        'clientes',
        ['id', 'asc'],
        { id: [id] },
        ['cidade.estado', 'categoria'],
      );
      if (data.data.length > 0) {
        setId(data.data[0].id);
        setComprador(data.data[0].comprador || null);
        setPrecisaNfe(
          data.data[0].precisa_nfe === null || data.data[0].precisa_nfe === undefined
            ? null
            : !!data.data[0].precisa_nfe,
        );
        setNome(data.data[0].nome);
        setCpf(data.data[0].cpf);
        setTelefone(data.data[0].telefone);
        setBairro(data.data[0].bairro);
        setLogradouro(data.data[0].logradouro);
        setNumero(data.data[0].numero);
        setRg(data.data[0].rg);
        setRazaoSocial(data.data[0].razao_social);
        setNomeFantasia(data.data[0].nome_fantasia);
        setCnpj(data.data[0].cnpj);
        setEmail(data.data[0].email);
        setCep(data.data[0].cep);
        setAniversario(data.data[0].dataAniversario ? moment(new Date(data.data[0].dataAniversario)).format('YYYY-MM-DD') : '');
        setCidade(data.data[0].cidade);
        setIe(data.data[0].inscricao_estadual);
        setIndIeDest(data.data[0].ind_ie_dest || IND_IE_DEST_NAO_CONTRIBUINTE);
        setCategoria(data.data[0].categoria);
        setIsFidelidade(data.data[0].is_fidelidade);
        setCarregando(false);
      } else {
        throw 'Este cliente não existe!';
      }
    } catch (erros) {
      enqueueSnackbar(`${erros}`, {
        variant: 'error',
      });
      handleCloseDialog(-1);
    }
  }

  function abrirBuscaSefaz() {
    if (consultandoSefaz) return;

    if (!cnpj && !cpf && !ie) {
      enqueueSnackbar('Preencha o CPF, o CNPJ ou a IE antes de buscar na SEFAZ!', {
        variant: 'warning',
      });
      return;
    }

    setIndiceOpcaoBuscaSefaz(0);
    setDialogoBuscaSefazAberto(true);
  }

  function getOpcoesBuscaSefaz() {
    const opcoes = [];
    if (cnpj) opcoes.push({ tipo: 'cnpj', label: `CNPJ (${cnpj})` });
    if (cpf) opcoes.push({ tipo: 'cpf', label: `CPF (${cpf})` });
    if (ie) opcoes.push({ tipo: 'ie', label: `IE (${ie})` });
    return opcoes;
  }

  async function buscarDadosSefaz(tipo) {
    if (consultandoSefaz) return;

    setDialogoBuscaSefazAberto(false);
    setConsultandoSefaz(true);
    try {
      const response = await createAPI('clientes_consultar_cadastro_sefaz', {
        cnpj: tipo === 'cnpj' ? cnpj : null,
        cpf: tipo === 'cpf' ? cpf : null,
        ie: tipo === 'ie' ? ie : null,
      });
      const { status, message, data } = response.data;

      if (status) {
        if (data.razao_social) setRazaoSocial(data.razao_social);
        if (data.nome_fantasia) setNomeFantasia(data.nome_fantasia);
        if (data.inscricao_estadual) setIe(data.inscricao_estadual);
        if (data.ind_ie_dest) setIndIeDest(data.ind_ie_dest);
        if (data.logradouro) setLogradouro(data.logradouro);
        if (data.numero) setNumero(data.numero);
        if (data.bairro) setBairro(data.bairro);
        if (data.cep) setCep(data.cep);

        if (data.cidade) {
          setCidade(data.cidade);
        } else if (data.cidade_nome_sefaz) {
          enqueueSnackbar(
            `A cidade "${data.cidade_nome_sefaz}" retornada pela SEFAZ não foi encontrada no cadastro, selecione manualmente.`,
            { variant: 'warning' },
          );
        }

        enqueueSnackbar('Dados encontrados na SEFAZ e preenchidos! Confira antes de salvar.', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(message || 'Não foi possível consultar a SEFAZ.', {
          variant: 'error',
        });
      }
    } catch (erros) {
      enqueueSnackbar('INTERNET: ERRO AO CONSULTAR A SEFAZ!', {
        variant: 'error',
      });
    } finally {
      setConsultandoSefaz(false);
    }
  }

  async function testarCadastroSefaz() {
    try {
      const response = await createAPI('teste-nfe-cliente-sem-persistencia', {
        nome,
        cpf,
        cnpj,
        inscricao_estadual: ie,
        razao_social: razaoSocial,
        nome_fantasia: nomeFantasia,
        logradouro,
        numero,
        bairro,
        cep,
        cidade_id: cidade ? cidade.id : null,
        ind_ie_dest: indIeDest,
        comprador,
      });
      const { status, autorizado, motivo, message } = response.data;

      if (!status) {
        enqueueSnackbar(message || 'Não foi possível testar o cadastro do cliente na SEFAZ.', {
          variant: 'error',
        });
        return false;
      }

      if (!autorizado) {
        enqueueSnackbar(
          motivo || 'Cadastro incompleto para emissão de NF-e — o cliente será salvo mesmo assim, revise os dados fiscais.',
          { variant: 'warning' },
        );
      }

      return !!autorizado;
    } catch (erros) {
      enqueueSnackbar('INTERNET: ERRO AO TESTAR O CADASTRO NA SEFAZ!', {
        variant: 'error',
      });
      return false;
    }
  }

  async function handleSalvar() {
    if (!erroExistente) {
      setCarregando(true);
      try {
        // só testa a emissão de NFe quando o cadastro precisa disso; senão
        // não faz sentido gastar a chamada na SEFAZ
        const isCadastroCompletoSefaz = precisaNfe ? await testarCadastroSefaz() : false;

        let idLocal = -1;
        if (id <= 0) {
          const response = await createAPI(
            'clientes_verifica_duplicidade',
            {
              nome,
              cpf,
              telefone,
              bairro,
              logradouro,
              numero,
              rg,
              razao_social: razaoSocial,
              nome_fantasia: nomeFantasia,
              cnpj,
              email,
              inscricao_estadual: ie,
              cep,
              cidade_id: cidade ? cidade.id : null,
              categoria_cliente_id: categoria ? categoria.id : null,
              user_id,
              is_fidelidade: isFidelidade,
              ind_ie_dest: indIeDest,
              comprador,
              precisa_nfe: precisaNfe,
              is_cadastro_completo_sefaz: isCadastroCompletoSefaz,
              ...decompoeData(aniversario),
            },
          );
          setCarregando(false);
          const { status, message, data } = response.data
          if(status) {
            enqueueSnackbar('Cliente criado com sucesso!', {
              variant: 'success',
            });
            idLocal = data ? data.id : -1;
            handleCloseDialog(idLocal);
          } else {
            enqueueSnackbar(`${message}`, {
              variant: 'error',
            });
          }

        } else {
          const response = await updateAPI(
            'clientes_verifica_duplicidade',
            id,
            {
              nome,
              cpf,
              telefone,
              bairro,
              logradouro,
              numero,
              rg,
              razao_social: razaoSocial,
              nome_fantasia: nomeFantasia,
              cnpj,
              email,
              inscricao_estadual: ie,
              cep,
              cidade_id: cidade ? cidade.id : null,
              categoria_cliente_id: categoria ? categoria.id : null,
              is_fidelidade: isFidelidade,
              ind_ie_dest: indIeDest,
              comprador,
              precisa_nfe: precisaNfe,
              is_cadastro_completo_sefaz: isCadastroCompletoSefaz,
              ...decompoeData(aniversario),
            },
          );
          setCarregando(false);
          const { status, message, data } = response.data
          if(status) {
            enqueueSnackbar('Cliente atualizado com sucesso!', {
              variant: 'success',
            });
            idLocal = id;
            handleCloseDialog(idLocal);
          } else {
            enqueueSnackbar(`${message}`, {
              variant: 'error',
            });
          }

        }

      } catch (erros) {
        setCarregando(false);
        enqueueSnackbar(`${erros}`, {
          variant: 'error',
        });
        handleCloseDialog(-1);
      }
    } else {
      enqueueSnackbar('Existem erros no formulário!', {
        variant: 'error',
      });
    }
  }

  useImperativeHandle(ref, () => ({
    handleCreate() {
      setOpen(true);
      setCarregando(true);
      reiniciar();
    },
    handleEdit(id) {
      setOpen(true);
      setCarregando(true);
      setId(id);
      getDataResource(id);
    },
  }));

  function fidelidade() {
    if (refDialogoAutorizacao.current) {
      refDialogoAutorizacao.current.handleOpen();
    }
  }

  // toda vez que CPF ou CNPJ muda, recalcula "comprador": se só um dos dois
  // está preenchido, escolhe ele automaticamente; se os dois (ou nenhum)
  // estão preenchidos, fica vazio e obriga a pessoa a escolher/confirmar
  function recalcularComprador(novoCpf, novoCnpj) {
    if (novoCpf && !novoCnpj) return COMPRADOR_CPF;
    if (novoCnpj && !novoCpf) return COMPRADOR_CNPJ;
    return null;
  }

  function handleCpfChange(value) {
    setCpf(value);
    setComprador(recalcularComprador(value, cnpj));
  }

  function handleCnpjChange(value) {
    setCnpj(value);
    setComprador(recalcularComprador(cpf, value));
  }

  function handleActions(action) {
    switch (action) {
      case FECHAR_VENDA_ACTION:
        handleSalvar();
        break;
      case CANCELAR_VENDA_ACTION:
        handleCloseDialog(-1);
        break;
      case ESC_ACTION:
        handleCloseDialog(-1);
        break;
      case CONSULTOR_PRECOS_ACTION:
        openConsultorPrecos();
        break;
      case FIDELIDADE_ACTION:
        //fidelidade();
        break;
      case BUSCAR_SEFAZ_ACTION:
        abrirBuscaSefaz();
        break;
      default:
        break;
    }
  }

  function getRefNextInput(index) {
    let position = -1;
    switch (index) {
      case PRECISA_NFE_POSITION:
        position = COMPRADOR_POSITION;
        break;
      case COMPRADOR_POSITION:
        position = 0;
        break;
      case 0:
        position = 1;
        break;
      case 1:
        position = 2;
        break;
      case 2:
        position = 3;
        break;
      case 3:
        position = 4;
        break;
      case 4:
        position = 5;
        break;
      case 5:
        position = 6;
        break;
      case 6:
        position = 7;
        break;
      case 7:
        position = 8;
        break;
      case 8:
        position = 9;
        break;
      case 9:
        position = 10;
        break;
      case 10:
        position = 16;
        break;
      case 16:
        position = 11;
        break;
      case 11:
        position = 12;
        break;
      case 12:
        position = 13;
        break;
      case 13:
        position = 14;
        break;
      case 14:
        position = 15;
        break;
      default:
        break;
    }
    return position;
  }

  function handleNextInput(index) {
    // alguns campos somem conforme o tipo de comprador (PF/PJ); pula pra
    // frente na cadeia até achar o próximo campo realmente montado
    let position = getRefNextInput(index);
    const visitados = new Set();
    while (
      position !== -1 &&
      !visitados.has(position) &&
      !(refs.current[position] && refs.current[position].current)
    ) {
      visitados.add(position);
      position = getRefNextInput(position);
    }

    if (position === -1 || !(refs.current[position] && refs.current[position].current)) {
      refs.current[PRECISA_NFE_POSITION].current.focus();
      if (refs.current[PRECISA_NFE_POSITION].current.select) {
        refs.current[PRECISA_NFE_POSITION].current.select();
      }
    } else {
      refs.current[position].current.focus();
      if (refs.current[position].current.select) refs.current[position].current.select();
    }
  }

  function handleKey(keyCode, keyName) {
    handleActions(keyName);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      disableBackdropClick
      disableEscapeKeyDown
      fullScreen
    >
      <DialogTitle id="form-dialog-title" style={{ color: 'white', backgroundColor: modoOffline ? SECONDARY_COLOR_OFFLINE : SECONDARY_COLOR, textAlign: 'center' }}>
        {id > 0 ? 'Editar Cliente' : 'Novo Cliente'}
      </DialogTitle>
      <DialogContent
        dividers
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '10px 10px 16px 10px',
            padding: '14px 20px',
            borderRadius: '16px',
            background: isFidelidade
              ? 'linear-gradient(135deg, #00c853 0%, #69f0ae 100%)'
              : 'linear-gradient(135deg, #546e7a 0%, #90a4ae 100%)',
            boxShadow: isFidelidade
              ? '0 4px 18px rgba(0, 200, 83, 0.35)'
              : '0 4px 18px rgba(0,0,0,0.35)',
            border: `2px solid ${isFidelidade ? '#00e676' : '#90a4ae'}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ fontSize: '32px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}>
              {isFidelidade ? '⭐' : '👤'}
            </div>
            <div>
              <Typography
                variant="subtitle1"
                style={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: 1.2,
                  textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              >
                {isFidelidade ? 'Cliente Fidelidade' : 'Cliente Padrão'}
              </Typography>
              <Typography
                variant="caption"
                style={{ color: 'rgba(255,255,255,0.88)', fontSize: '12px' }}
              >
                {isFidelidade
                  ? 'Inscrito no Programa de Fidelidade'
                  : 'Não inscrito no Programa de Fidelidade'}
              </Typography>
            </div>
          </div>

  {/*         <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: '12px',
              padding: '6px 14px',
            }}
          >
            <Typography
              variant="caption"
              style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500, fontSize: '12px' }}
            >
              {isFidelidade ? 'Remover' : 'Adicionar'} — pressione
            </Typography>
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.28)',
                border: '1px solid rgba(255,255,255,0.5)',
                borderRadius: '7px',
                padding: '2px 9px',
              }}
            >
              <Typography
                variant="caption"
                style={{
                  color: 'white',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  letterSpacing: '0.5px',
                }}
              >
                {FIDELIDADE_ACTION}
              </Typography>
            </div>
          </div> */}
        </div>
        <div style={{ height: '100%' }}>
          <Box
            display={{ xs: 'block', sm: 'flex' }}
            flexDirection="column"
            marginBottom="30px"
            height="100%"
          >
            {carregando ? (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px',
              }}
              >
                <CircularProgress />
              </div>
            )
              : (
                <Box p="1em">
                  <Box display="flex" justifyContent="flex-end" mb="1em">
                    <div
                      onClick={abrirBuscaSefaz}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: !cnpj && !cpf && !ie ? '#f5f5f5' : '#e3f2fd',
                        border: `1px solid ${!cnpj && !cpf && !ie ? '#bdbdbd' : '#1565c0'}`,
                        borderRadius: '12px',
                        padding: '6px 14px',
                        cursor: consultandoSefaz || (!cnpj && !cpf && !ie) ? 'default' : 'pointer',
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          color: consultandoSefaz || (!cnpj && !cpf && !ie) ? '#9e9e9e' : '#1565c0',
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      >
                        {consultandoSefaz ? 'Consultando SEFAZ...' : 'Buscar dados na SEFAZ — pressione'}
                      </Typography>
                      {!consultandoSefaz && (
                        <div
                          style={{
                            backgroundColor: !cnpj && !cpf && !ie ? '#e0e0e0' : '#bbdefb',
                            border: `1px solid ${!cnpj && !cpf && !ie ? '#bdbdbd' : '#1565c0'}`,
                            borderRadius: '7px',
                            padding: '2px 9px',
                          }}
                        >
                          <Typography
                            variant="caption"
                            style={{
                              color: !cnpj && !cpf && !ie ? '#9e9e9e' : '#1565c0',
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {BUSCAR_SEFAZ_ACTION}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Box>

                  <Box display="flex">
                    <Box flex={2} mr="1em">
                      <Box
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '10px',
                          padding: '16px',
                          marginBottom: '1.5em',
                          backgroundColor: '#fafafa',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Box display="flex" style={{ gap: '24px' }}>
                          <Box style={{ flex: '0 0 calc(50% - 12px)', boxSizing: 'border-box' }}>
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }} gutterBottom>
                              Este cadastro vai ser usado para emitir NFe?
                            </Typography>
                            <Typography variant="body2" style={{ color: '#757575', marginBottom: '12px' }}>
                              Se marcar "Sim", endereço completo (e razão social ou nome fantasia,
                              se empresa) passam a ser obrigatórios pra emissão da nota.
                            </Typography>
                            <GrupoCardEscolha
                              ref={refs.current[PRECISA_NFE_POSITION]}
                              handleEnter={() => handleNextInput(PRECISA_NFE_POSITION)}
                              handleKey={handleKey}
                              valorSelecionado={precisaNfe}
                              onChange={(valor) => setPrecisaNfe(valor)}
                              opcoes={[
                                { valor: true, label: 'Sim' },
                                { valor: false, label: 'Não' },
                              ]}
                              autoFocus
                            />
                            {erroPrecisaNfe !== '' && (
                              <Typography variant="caption" style={{ color: '#f44336', display: 'block', marginTop: '8px' }}>
                                {erroPrecisaNfe}
                              </Typography>
                            )}
                          </Box>

                          {precisaNfe && (
                            <Box flex={1} style={{ borderLeft: '1px solid #e0e0e0', paddingLeft: '24px' }}>
                              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }} gutterBottom>
                                Qual documento vai na NFe?
                              </Typography>
                              <Typography variant="body2" style={{ color: '#757575', marginBottom: '12px' }}>
                                Define qual documento (CPF ou CNPJ) identifica o destinatário na
                                nota fiscal.
                              </Typography>
                              <GrupoCardEscolha
                                ref={refs.current[COMPRADOR_POSITION]}
                                handleEnter={() => handleNextInput(COMPRADOR_POSITION)}
                                handleKey={handleKey}
                                valorSelecionado={comprador}
                                onChange={(valor) => setComprador(valor)}
                                opcoes={[
                                  { valor: COMPRADOR_CPF, label: 'CPF', disabled: !cpf },
                                  { valor: COMPRADOR_CNPJ, label: 'CNPJ', disabled: !cnpj },
                                ]}
                              />
                              {erroComprador !== '' && (
                                <Typography variant="caption" style={{ color: '#f44336', display: 'block', marginTop: '8px' }}>
                                  {erroComprador}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Typography variant="h6" gutterBottom>
                        Dados Pessoais
                      </Typography>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <TextInput
                            name={inputs[0].nome}
                            ref={refs.current[NOME_POSITION]}
                            handleEnter={() => handleNextInput(NOME_POSITION)}
                            label={inputs[0].label}
                            handleKey={handleKey}
                            value={nome}
                            onChange={(value) => setNome(value.target.value)}
                            error={erros[0] !== ''}
                            helperText={erros[0]}
                            fullWidth
                            autoCompleteState="new-password"
                          />
                        </Box>
                        <Box flex={1} ml="0.5em">
                          <CpfInput
                            name={inputs[1].nome}
                            ref={refs.current[CPF_POSITION]}
                            handleEnter={() => handleNextInput(CPF_POSITION)}
                            label={inputs[1].label}
                            handleKey={handleKey}
                            value={cpf}
                            onChange={(value) => handleCpfChange(value.target.value)}
                            error={erros[1] !== ''}
                            helperText={erros[1]}
                            fullWidth
                            autoCompleteState="new-password"
                          />
                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <TelefoneInput
                            name={inputs[2].nome}
                            ref={refs.current[TELEFONE_POSITION]}
                            handleEnter={() => handleNextInput(TELEFONE_POSITION)}
                            label={inputs[2].label}
                            handleKey={handleKey}
                            value={telefone}
                            onChange={(value) => setTelefone(value.target.value)}
                            error={erros[2] !== ''}
                            helperText={erros[2]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                        <Box flex={1} ml="0.5em">
                          <TextInput
                            name={inputs[10].nome}
                            ref={refs.current[EMAIL_POSITION]}
                            handleEnter={() => handleNextInput(EMAIL_POSITION)}
                            label={inputs[10].label}
                            handleKey={handleKey}
                            value={email}
                            onChange={(value) => setEmail(value.target.value)}
                            error={erros[10] !== ''}
                            helperText={erros[10]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <DateInput
                            name={inputs[12].nome}
                            ref={refs.current[ANIVERSARIO_POSITION]}
                            handleEnter={() => handleNextInput(ANIVERSARIO_POSITION)}
                            label={inputs[12].label}
                            handleKey={handleKey}
                            value={aniversario}
                            onChange={(value) => setAniversario(value.target.value)}
                            error={erros[12] !== ''}
                            helperText={erros[12]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                        <Box flex={1} ml="0.5em">
                          <TextInput
                            name={inputs[6].nome}
                            ref={refs.current[RG_POSITION]}
                            handleEnter={() => handleNextInput(RG_POSITION)}
                            label={inputs[6].label}
                            handleKey={handleKey}
                            value={rg}
                            onChange={(value) => setRg(value.target.value)}
                            error={erros[6] !== ''}
                            helperText={erros[6]}
                            fullWidth
                            autoCompleteState="new-password"
                          />
                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <AutoCompleteRemoto
                            name={inputs[15].nome}
                            ref={refs.current[CATEGORIA_POSITION]}
                            handleEnter={() => handleNextInput(CATEGORIA_POSITION)}
                            resource={inputs[15].resource}
                            nested={inputs[15].nested}
                            filters={inputs[15].filters}
                            label={inputs[15].label}
                            handleKey={handleKey}
                            value={categoria}
                            onChange={(value) => setCategoria(value)}
                            error={erros[15] !== ''}
                            helperText={erros[15]}
                            toString={false}
                            fullWidth
                            getOptionSelected={(option, value) => option.nome === value.nome}
                            getOptionLabel={(option) => `${option.nome}`}
                          />
                        </Box>
                        <Box flex={1} ml="0.5em" />
                      </Box>

                      <Box mt="1em" />

                      <Typography variant="h6" gutterBottom>
                        Dados Empresariais
                      </Typography>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <TextInput
                            name={inputs[7].nome}
                            ref={refs.current[RAZAO_SOCIAL_POSITION]}
                            handleEnter={() => handleNextInput(RAZAO_SOCIAL_POSITION)}
                            label={inputs[7].label}
                            handleKey={handleKey}
                            value={razaoSocial}
                            onChange={(value) => setRazaoSocial(value.target.value)}
                            error={erros[7] !== ''}
                            helperText={erros[7]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                        <Box flex={1} ml="0.5em">
                          <TextInput
                            name={inputs[8].nome}
                            ref={refs.current[NOME_FANTASIA_POSITION]}
                            handleEnter={() => handleNextInput(NOME_FANTASIA_POSITION)}
                            label={inputs[8].label}
                            handleKey={handleKey}
                            value={nomeFantasia}
                            onChange={(value) => setNomeFantasia(value.target.value)}
                            error={erros[8] !== ''}
                            helperText={erros[8]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <CnpjInput
                            name={inputs[9].nome}
                            ref={refs.current[CNPJ_POSITION]}
                            handleEnter={() => handleNextInput(CNPJ_POSITION)}
                            label={inputs[9].label}
                            handleKey={handleKey}
                            value={cnpj}
                            onChange={(value) => handleCnpjChange(value.target.value)}
                            error={erros[9] !== ''}
                            helperText={erros[9]}
                            fullWidth
                            autoCompleteState="new-password"
                          />
                        </Box>
                        <Box flex={1} ml="0.5em">
                          <IEInput
                            name={inputs[14].nome}
                            ref={refs.current[IE_POSITION]}
                            handleEnter={() => handleNextInput(IE_POSITION)}
                            label={inputs[14].label}
                            handleKey={handleKey}
                            value={ie}
                            onChange={(value) => setIe(value.target.value)}
                            error={erros[14] !== ''}
                            helperText={erros[14]}
                            fullWidth
                            autoCompleteState="new-password"
                          />
                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <AutoCompleteLocal
                            name={inputs[16].nome}
                            ref={refs.current[IND_IE_DEST_POSITION]}
                            handleEnter={() =>
                              handleNextInput(IND_IE_DEST_POSITION)
                            }
                            handleKey={handleKey}
                            value={
                              optionsIndIeDest.find(
                                (opcao) => opcao.value === indIeDest,
                              ) || null
                            }
                            onChangeLocal={(opcao) =>
                              setIndIeDest(opcao ? opcao.value : null)
                            }
                            campo="label"
                            options={optionsIndIeDest}
                            label={inputs[16].label}
                            fullWidth
                          />
                        </Box>
                        <Box flex={1} ml="0.5em" />
                      </Box>

                      <Box mt="1em" />

                      <Typography variant="h6" gutterBottom>
                        Endereço
                      </Typography>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <AutoCompleteRemoto
                            name={inputs[13].nome}
                            ref={refs.current[CIDADE_POSITION]}
                            handleEnter={() => handleNextInput(CIDADE_POSITION)}
                            resource={inputs[13].resource}
                            nested={inputs[13].nested}
                            filters={inputs[13].filters}
                            label={inputs[13].label}
                            handleKey={handleKey}
                            value={cidade}
                            onChange={(value) => setCidade(value)}
                            error={erros[13] !== ''}
                            helperText={erros[13]}
                            toString={false}
                            fullWidth
                            getOptionSelected={(option, value) => option.nome === value.nome}
                            getOptionLabel={(option) => `${option.nome} - ${option.estado.sigla}`}
                          />
                        </Box>
                        <Box flex={1} ml="0.5em">
                          <TextInput
                            name={inputs[3].nome}
                            ref={refs.current[BAIRRO_POSITION]}
                            handleEnter={() => handleNextInput(BAIRRO_POSITION)}
                            label={inputs[3].label}
                            handleKey={handleKey}
                            value={bairro}
                            onChange={(value) => setBairro(value.target.value)}
                            error={erros[3] !== ''}
                            helperText={erros[3]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <TextInput
                            name={inputs[4].nome}
                            ref={refs.current[LOGRADOURO_POSITION]}
                            handleEnter={() => handleNextInput(LOGRADOURO_POSITION)}
                            label={inputs[4].label}
                            handleKey={handleKey}
                            value={logradouro}
                            onChange={(value) => setLogradouro(value.target.value)}
                            error={erros[4] !== ''}
                            helperText={erros[4]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                        <Box flex={1} ml="0.5em">
                          <TextInput
                            name={inputs[5].nome}
                            ref={refs.current[NUMERO_POSITION]}
                            handleEnter={() => handleNextInput(NUMERO_POSITION)}
                            label={inputs[5].label}
                            handleKey={handleKey}
                            value={numero}
                            onChange={(value) => setNumero(value.target.value)}
                            error={erros[5] !== ''}
                            helperText={erros[5]}
                            fullWidth
                            autoCompleteState="new-password"
                          />

                        </Box>
                      </Box>

                      <Box display="flex">
                        <Box flex={1} mr="0.5em">
                          <CepInput
                            name={inputs[11].nome}
                            ref={refs.current[CEP_POSITION]}
                            handleEnter={() => handleNextInput(CEP_POSITION)}
                            label={inputs[11].label}
                            handleKey={handleKey}
                            value={cep}
                            onChange={(value) => setCep(value.target.value)}
                            error={erros[11] !== ''}
                            helperText={erros[11]}
                            fullWidth
                            autoCompleteState="new-password"
                          />
                        </Box>
                        <Box flex={1} ml="0.5em" />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>
        </div>
      </DialogContent>
      <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={() => handleCloseDialog(-1)}
          color="secondary"
          variant="contained"
          tabindex="-1"
        >
          {`Cancelar (${CANCELAR_VENDA_ACTION})`}
        </Button>
        <div className={classes.wrapper}>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              handleSalvar();
            }}
            disabled={erroExistente || carregando}
            tabindex="-1"
          >
            {`Cadastrar (${FECHAR_VENDA_ACTION})`}
          </Button>
          {carregando && <CircularProgress size={24} className={classes.buttonProgress} />}
        </div>
      </DialogActions>
      <DialogoAutorizacaoQrcode2
        ref={refDialogoAutorizacao}
        handleSalvar={() => {
          setIsFidelidade(!isFidelidade)
        }}
        enqueueSnackbar={enqueueSnackbar}
        mensagemSucesso={
          isFidelidade
            ? "Cliente Removido do sistema de Fidelidade"
            : "Cliente Adicionado ao sistema de Fidelidade"
        }
        title={
          isFidelidade ? "Remover do Programa de Fidelidade" : "Adicionar ao Programa de Fidelidade"
        }
        openConsultorPrecos={openConsultorPrecos}
        usar_biometria={usar_biometria}
      />
      <Dialog
        open={dialogoBuscaSefazAberto}
        onClose={() => setDialogoBuscaSefazAberto(false)}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>Buscar dados na SEFAZ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: '#757575', marginBottom: '12px' }}>
            {`Use as setas pra escolher qual documento preenchido será usado na consulta. Enter ou ${FECHAR_VENDA_ACTION} pra confirmar.`}
          </Typography>
          {dialogoBuscaSefazAberto && (() => {
            const opcoes = getOpcoesBuscaSefaz();
            return (
              <Box display="flex" flexDirection="column" style={{ gap: '10px' }}>
                {opcoes.map((opcao, index) => {
                  const selecionado = index === indiceOpcaoBuscaSefaz;
                  return (
                    <Box
                      key={opcao.tipo}
                      ref={(el) => { refsOpcaoBuscaSefaz.current[index] = el; }}
                      tabIndex={0}
                      onFocus={() => setIndiceOpcaoBuscaSefaz(index)}
                      onClick={() => buscarDadosSefaz(opcao.tipo)}
                      style={{
                        cursor: 'pointer',
                        padding: '12px',
                        borderRadius: '8px',
                        backgroundColor: selecionado ? '#1565c0' : '#e3f2fd',
                        border: selecionado ? '3px solid #0d47a1' : '3px solid transparent',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: selecionado ? 'white' : '#1565c0',
                        boxSizing: 'border-box',
                        outline: 'none',
                      }}
                    >
                      {opcao.label}
                      {selecionado ? ' ✓' : ''}
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoBuscaSefazAberto(false)}>
            {`Cancelar (${CANCELAR_VENDA_ACTION})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
    
  );
});

export default SidebarInputs;
